import { useMemo } from 'react';

import dayjs from 'dayjs';

import { useIsClusterDemoMode } from '@cast/infrastructure-demo';
import { TimeRange } from '@cast/shared-types';
import {
  defaultQueryParamKeys,
  DateRangeFilter,
  RangePickerQueryParamKeys,
} from '@cast/shared-date-ui';
import { useTimeRangeSelection } from 'hooks/useTimeRangeSelection';

export const QUERY_PARAM_KEYS: RangePickerQueryParamKeys =
  defaultQueryParamKeys;

export const PRESENT_FILTER_PRESETS = [
  DateRangeFilter.LastHour,
  DateRangeFilter.Last6Hours,
  DateRangeFilter.Last24Hours,
  DateRangeFilter.Last7Days,
];

type UseDboTimeRangeSelectionResult = ReturnType<
  typeof useTimeRangeSelection
> & { range: TimeRange };

const floorToMinute = (iso: string) =>
  dayjs(iso).startOf('minute').toISOString();

export const useDboTimeRangeSelection = (): UseDboTimeRangeSelectionResult => {
  const isClusterDemoMode = useIsClusterDemoMode();

  const selection = useTimeRangeSelection({
    queryParamKeys: QUERY_PARAM_KEYS,
    initialState: {
      preset: DateRangeFilter.LastHour,
      timeZone: isClusterDemoMode ? 'UTC' : undefined,
    },
  });

  const range = useMemo(() => {
    const resolved = selection.getRange();
    const isExplicitRange = Boolean(selection.from && selection.to);

    // Ranges derived from presets drift with `now` and must be bucketed;
    // explicitly picked custom ranges are left untouched.
    return isExplicitRange
      ? resolved
      : { from: floorToMinute(resolved.from), to: floorToMinute(resolved.to) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selection.key]);

  return { ...selection, range };
};
